import {useTranslation} from "react-i18next";
import {useContext, useEffect, useRef} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import FormError from "../../components/form/FormError.jsx";
import ServiceLayout from "../../layout/ServiceLayout.jsx";
import {AuthenticationApi} from "../../api/authority/generated/index";

const inFlightConfirmations = new Map();

export default function ContinueFromInvitation() {

    let {t} = useTranslation();
    let navigate = useNavigate();
    const location = useLocation();

    let [searchParams] = useSearchParams();
    let token = searchParams.get('token');

    let {getAuthorityConfiguration, setCurrentIdentity} = useContext(GlobalContext);
    let [state, setDownloading, , setError] = useDownloadState();
    const handlersRef = useRef({navigate, setCurrentIdentity, setDownloading, setError});
    handlersRef.current = {navigate, setCurrentIdentity, setDownloading, setError};

    useEffect(() => {
        // Identity changes can remount this route before BrowserRouter commits the
        // success navigation. An obsolete route must not consume the token again.
        if (location.pathname !== window.location.pathname || location.search !== window.location.search) return;

        let active = true;

        handlersRef.current.setDownloading(true);
        confirmInvitation({
            api: new AuthenticationApi(getAuthorityConfiguration()),
            token,
            isActive: () => active,
            setCurrentIdentity: (identity) => handlersRef.current.setCurrentIdentity(identity),
            navigate: (...args) => handlersRef.current.navigate(...args),
            setError: (error) => handlersRef.current.setError(error),
            setDownloading: (value) => handlersRef.current.setDownloading(value),
        });

        return () => {
            active = false;
        };
    }, [getAuthorityConfiguration, location.pathname, location.search, token]);

    return (
        <LoadingComponent loading={state.downloading}>
            <ServiceLayout title={t('auth.invitation-confirmation')}>
                {state.error && <FormError error={state.error}/>}
            </ServiceLayout>
        </LoadingComponent>
    );
}

export async function confirmInvitation({
    api,
    token,
    isActive,
    setCurrentIdentity,
    navigate,
    setError,
    setDownloading,
}) {
    try {
        const newIdentity = await getInvitationConfirmation(api, token);
        if (!isActive()) return;

        setCurrentIdentity(newIdentity);
        navigate('/');
    } catch (error) {
        if (isActive()) setError(error);
    } finally {
        if (isActive()) setDownloading(false);
    }
}

export function getInvitationConfirmation(api, token) {
    const existingConfirmation = inFlightConfirmations.get(token);
    if (existingConfirmation) return existingConfirmation;

    const confirmation = Promise.resolve().then(() => api.confirmInvitation({token}));
    inFlightConfirmations.set(token, confirmation);
    confirmation.then(
        () => clearSettledConfirmation(token, confirmation),
        () => clearSettledConfirmation(token, confirmation),
    );

    return confirmation;
}

function clearSettledConfirmation(token, confirmation) {
    if (inFlightConfirmations.get(token) === confirmation) {
        inFlightConfirmations.delete(token);
    }
}

export function shouldKeepInvitationRoute(pathname) {
    return pathname === '/invite';
}
